import BackButton from '@/components/BackButton';
import CtaButton from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import ErrorMessage from '@/components/ErrorMessage';
import PasswordInput from '@/components/Forms/PasswordInput';
import TextInput from '@/components/Forms/TextInput';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import RightCheveron from '@/components/Icons/RightCheveron';
import PageLayout from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useHandleErrorLogic from '@/hooks/useHandleErrorLogic';
import { User } from '@/lib/interfaces';
import {
  CLIENT_FORGOT_PASSWORD_URL,
  CLIENT_LOGIN_URL,
} from '@/utils/constants';
import { LoginFormInitialValues, LoginSchema } from '@/utils/forms';
import { login } from '@/utils/http/userAuthRequests';
import { routeUserAfterLogin } from '@/utils/v2HelperMethods';
import { verifyUser } from '@/utils/v2HttpRequests';
import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { redirect = null } = query;
  return { props: { redirect } };
};

export default function LoginPage({ redirect }: { redirect: string | null }) {
  const router = useRouter();
  const [err, setErr] = useHandleErrorLogic();

  return (
    <PageLayout>
      <ErrorMessage error={err} />
      <NavBarLightTheme sticky={false} />
      <TwelveColGrid
        my="mt-3.2 mb-4.8 lgt:mt-6.4"
        addClassStyles="max-w-screen-lgt "
      >
        <div className="flex justify-center col-span-12">
          <BackButton href={CLIENT_LOGIN_URL} />
        </div>

        <h3 className="col-span-12 mt-1.6 text-center text-black-100 copy-l">
          LOGIN TO OLEA
        </h3>

        <div className="mt-7.2 lgt:mt-[8.8rem] col-span-12 lgt:col-start-5 lgt:col-span-4">
          <Formik
            validationSchema={LoginSchema}
            initialValues={LoginFormInitialValues}
            onSubmit={async (values, actions) => {
              try {
                const { identifier, password } = values;
                await login({
                  identifier,
                  password,
                });
                const user: User = await verifyUser();
                actions.setSubmitting(false);
                await routeUserAfterLogin({ user, router, redirect });
              } catch (error) {
                if (axios.isAxiosError(error)) {
                  const errorMessage = error.response?.data.error.message;
                  setErr(errorMessage);
                } else {
                  setErr(false);
                }
                actions.setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, isValid }) => (
              <Form className="flex flex-col justify-between">
                <div>
                  <Field
                    type="email"
                    name="identifier"
                    placeholder=" "
                    labelCopy="Email"
                    component={TextInput}
                  />
                  <Field
                    name="password"
                    placeholder=" "
                    labelCopy="Password"
                    component={PasswordInput}
                  />
                </div>
                <div className="absolute flex flex-col items-center justify-center mx-auto left-0 right-0 bottom-6.4">
                  <CtaButton
                    height="h-4.8"
                    width="w-34.4 lgt:w-36.0"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? (
                      <>
                        <CtaText>PROCESSING</CtaText>
                        <LoadingSpinner />
                      </>
                    ) : (
                      <>
                        <CtaText>LOGIN</CtaText>
                        <RightCheveron />
                      </>
                    )}
                  </CtaButton>
                  <div className="font-medium text-center text-xs w-34.4 mt-1.6">
                    <Link href={CLIENT_FORGOT_PASSWORD_URL}>
                      FORGOT PASSWORD?
                    </Link>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </TwelveColGrid>
    </PageLayout>
  );
}
